/** @jsx jsx */
import {
  Modal,
  ModalActionButton,
  ModalContainer,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalHeading,
  ModalProps,
  Stack,
} from "@eworkit/iris";
import { Formik, FormikProps } from "formik";
import { FC, useContext } from "react";
import { useTranslation } from "react-i18next";
import { Container, jsx } from "theme-ui";
import * as Yup from "yup";

import { VALIDATION_TYPES } from "@/components/Formik";
import { Group } from "@/components/Group";
import { UserContext } from "@/components/User";
import { IConsultantProfile, putConsultantProfileLocation } from "@/data/consultant-profiles";
import { ILocation } from "@/data/locations";
import { FormikForm } from "@/formikComponents";

import { FormikTypeaheadLocationHints } from "../components/Formik";
import { useToasts } from "../components/Toast/useToasts";

interface AddLocationToProfileModalProps extends ModalProps {
  profile: IConsultantProfile;
}

interface ILocationFormValue {
  location: ILocation;
}

const validationSchema = Yup.object().shape({
  location: Yup.object().required(VALIDATION_TYPES.REQUIRED),
});

export const AddLocationToProfileModal: FC<AddLocationToProfileModalProps> = ({ profile, ...modalProps }) => {
  const { t } = useTranslation("session");
  const userContext = useContext(UserContext);
  const { addToast } = useToasts();

  const handleSubmit = async (values: ILocationFormValue) => {
    try {
      const updatedProfile = await putConsultantProfileLocation({ consultantProfileId: profile.id }, values.location);
      userContext.updateContextUserProfile(updatedProfile);
      addToast({ text: t("CONSULTANT_PROFILES.DETAIL.SUCCESS_MESSAGE"), type: "success" });
      modalProps.dialogState.hide();
    } catch (error) {
      addToast({
        text: t("CONSULTANT_PROFILES.DETAIL.ABOUT_ME.ERROR"),
        type: "warning",
      });
    }
  };

  return (
    <Modal aria-label={t("CONSULTANT_PROFILES.COMPLETENESS.CONTENT.PROFILE.LOCATION.MODAL.TITLE")} {...modalProps}>
      <ModalContainer css={{ overflow: "visible !important" }}>
        <ModalHeader
          sx={{ backgroundColor: "transparent !important" }}
          onCloseClick={modalProps.dialogState.hide}
          headerPlaceholder={
            <ModalHeading>{t("CONSULTANT_PROFILES.COMPLETENESS.CONTENT.PROFILE.LOCATION.MODAL.TITLE")}</ModalHeading>
          }
        />

        <Formik
          initialValues={{ location: profile.location }}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}>
          {(form: FormikProps<ILocationFormValue>) => (
            <FormikForm>
              <ModalContent sx={{ overflowY: "visible" }}>
                <Stack gap="sm">
                  <Container>{t(`CONSULTANT_PROFILES.COMPLETENESS.CONTENT.PROFILE.LOCATION.MODAL.DESC`)}</Container>
                  <Group label={t("CONSULTANT_PROFILES.DETAIL.LOCATION.LABEL")} direction="column">
                    <FormikTypeaheadLocationHints
                      name="location"
                      testName="location"
                      value={form.values.location}
                      valueKey="name"
                      labelKey="name"
                    />
                  </Group>
                </Stack>
              </ModalContent>

              <ModalFooter>
                <ModalActionButton
                  type="button"
                  variant="primary"
                  onClick={form.submitForm}
                  loading={form.isSubmitting}
                  disabled={form.isSubmitting}>
                  {t("CONSULTANT_PROFILES.COMPLETENESS.BUTTON_SAVE")}
                </ModalActionButton>
              </ModalFooter>
            </FormikForm>
          )}
        </Formik>
      </ModalContainer>
    </Modal>
  );
};
